import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import {
    DSection,
    RhDateInput,
    RhFormWrapper,
    RHTextField,
} from '../../../../../dum';
import {
    clearSalesModule,
    useUpdateOrderRequestDetailsMutation,
} from '../../../../../../services';
import { FIELD_LABELS, shortLabel } from '../../../../../../helpers';

interface IOrderRequestDetailsChange {
    notes?: string;
    estimated_delivery_date?: string | null;
}

interface IOrderRequestDetailsChangeFormProps {
    orderRequestId: number;
    currentNotes?: string | null;
    currentEstimatedDeliveryDate?: string | null;
    onSuccess: () => void;
}

// Lets sales users amend the optional, operational fields of a request (notes,
// estimated delivery date) without going through the full request upsert and
// its status-1 edit lock — mirroring OrderSaleDetailsChangeForm. The backend
// updateOrderRequestDetails endpoint is deliberately not status-locked.
export function OrderRequestDetailsChangeForm({
    orderRequestId,
    currentNotes,
    currentEstimatedDeliveryDate,
    onSuccess,
}: IOrderRequestDetailsChangeFormProps) {
    const [mutation, { loading }] = useUpdateOrderRequestDetailsMutation({
        update: (cache) => {
            clearSalesModule(cache);
        },
    });

    const initialValues = useMemo<IOrderRequestDetailsChange>(() => {
        return {
            notes: currentNotes || '',
            estimated_delivery_date: currentEstimatedDeliveryDate || null,
        };
    }, [currentNotes, currentEstimatedDeliveryDate]);

    const validationSchema: SchemaOf<IOrderRequestDetailsChange> = yup.object({
        notes: yup.string(),
        estimated_delivery_date: yup.string().notRequired().nullable(),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading}
            onSubmit={async (formData) => {
                try {
                    await mutation({
                        variables: {
                            OrderRequestDetailsInput: {
                                order_request_id: orderRequestId,
                                notes: formData.notes || '',
                                estimated_delivery_date:
                                    formData.estimated_delivery_date || null,
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
        >
            <DSection alwaysOpen>
                <RHTextField
                    name={'notes'}
                    label={shortLabel(FIELD_LABELS.notes)}
                    rows={3}
                />
                <RhDateInput
                    name={'estimated_delivery_date'}
                    label={shortLabel(FIELD_LABELS.estimated_delivery_date)}
                />
            </DSection>
        </RhFormWrapper>
    );
}
